import { v4 as uuidv4 } from 'uuid';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { VideoSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { getAllSegments, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';

/** Intro and outro are brand-kit bookends of the source clip, not body content. */
const BOOKEND_SEGMENT_IDS = new Set(['intro', 'outro']);

/**
 * Deep-clones a source clip's body segments so they can be spliced into another clip's timeline,
 * keeping scenes, video feeds, speaker labels and layoutChanges intact rather than flattening
 * anything down to a single video source.
 *
 * Both segment types are copied, in timeline order:
 *   - COMPOSITE — recorded content
 *   - VIDEO     — videos the user added to the source clip's body via "Add Video"
 *
 * Only intro/outro are excluded: they are the source clip's own brand-kit bookends, and the
 * target clip has (or does not want) its own. Order is preserved across both types so a source
 * clip that interleaves recorded content with uploaded video imports exactly as it plays.
 *
 * The imported segments are a point-in-time snapshot — later edits to the source clip do not
 * propagate. Translations are dropped so the importing clip's own language stays authoritative.
 */
export function getClipCompositeSegments(clip: Clip): Segment[] {
  const bodySegments = getAllSegments(clip).filter(
    segment =>
      !BOOKEND_SEGMENT_IDS.has(segment.id) &&
      (segment.type === SegmentType.COMPOSITE || segment.type === SegmentType.VIDEO)
  );

  return bodySegments.map(segment =>
    segment.type === SegmentType.VIDEO
      ? cloneVideoSegment(segment as VideoSegment, clip)
      : cloneCompositeSegment(segment as CompositeSegment, clip)
  );
}

/**
 * A VIDEO segment's length is re-derived from `segment_metadata.start`/`.end` in seconds on every
 * player-config regeneration, so those must survive the clone or the segment collapses to zero
 * frames on the target timeline. Older segments may carry only frame bounds, so fall back to them.
 */
function cloneVideoSegment(segment: VideoSegment, clip: Clip): VideoSegment {
  const cloned = structuredClone(segment);
  // Fresh id so the segment cannot collide with one already on the target timeline.
  cloned.id = uuidv4();

  const hasDurationInSeconds = !!cloned.segment_metadata && cloned.segment_metadata.end > cloned.segment_metadata.start;
  if (!hasDurationInSeconds) {
    const fps = getClipFPS(clip);
    const durationInSeconds = Math.max((segment.toFrame - segment.fromFrame) / fps, 0);
    cloned.segment_metadata = {
      ...cloned.segment_metadata,
      start: 0,
      end: durationInSeconds,
      deletes: cloned.segment_metadata?.deletes ?? {},
      maxVideoDuration: cloned.segment_metadata?.maxVideoDuration ?? durationInSeconds,
      src: cloned.segment_metadata?.src ?? cloned.video?.details?.src ?? ''
    };
  }

  return cloned;
}

function cloneCompositeSegment(segment: CompositeSegment, clip: Clip): CompositeSegment {
  const cloned = structuredClone(segment);
  // Fresh id so the segment cannot collide with one already on the target timeline.
  cloned.id = uuidv4();

  // A clip with no splits carries its editing state on asset_metadata rather than on the
  // segment, so synthesize the metadata the target timeline's splits path requires.
  if (!cloned.segment_metadata) {
    const meta = clip.asset_metadata;
    // start/end can both be 0 for an unedited clip, but filterVerySmallComposites drops any
    // composite shorter than 0.5s — fall back to duration so the import is never discarded.
    const clipDuration = meta.duration ?? 0;
    const segStart = meta.start ?? 0;
    const segEnd = meta.end && meta.end > segStart ? meta.end : clipDuration;
    cloned.segment_metadata = {
      start: segStart,
      end: segEnd,
      deletes: meta.deletes ?? {},
      edits: meta.edits ?? {},
      merges: meta.merges ?? [],
      excludes: meta.excludes ?? [],
      captions: meta.captions ?? [],
      fitMode: meta.fit,
      scenes: structuredClone(meta.scenes ?? cloned.scenes ?? []),
      duration: clipDuration,
      maxVideoDuration: meta.maxVideoDuration ?? segEnd,
      captions_lang: meta.original_lang
    };
  }

  // Ignore translations from the source clip — captions are kept in the original language only.
  cloned.segment_metadata.captions_lang = clip.asset_metadata.original_lang;

  return cloned;
}
